import type {
  BaseSelection,
  EditorConfig,
  LexicalNode,
  NodeKey,
  RangeSelection,
  SerializedElementNode,
  Spread,
} from 'lexical';
import {
  addClassNamesToElement,
  removeClassNamesFromElement,
} from '@lexical/utils';
import {
  $applyNodeReplacement,
  $isRangeSelection,
  ElementNode,
  DOMConversionMap,
  DOMExportOutput,
} from 'lexical';

export type SerializedMarkNode = Spread<
  {
    ids: Array<string>;
    bgColor?: string;
  },
  SerializedElementNode
>;

const NO_IDS: string[] = [];

/** @noInheritDoc */
export class MarkNode extends ElementNode {
  __ids: string[];
  __bgColor?: string;

  static getType(): string {
    return 'mark';
  }

  static clone(node: MarkNode): MarkNode {
    return new MarkNode(node.__ids, node.__key, node.__bgColor);
  }

  static importDOM(): DOMConversionMap {
    return {
      mark: (domNode) => {
        return {
          conversion: (domNode) => {
            const ids = domNode.getAttribute('data-ids')?.split(',') || [];
            const bgColor = domNode.getAttribute('data-highlight') || ''
            return { node: new MarkNode(ids, undefined, bgColor) };
          },
          priority: 1,
        };
      },
    };
  }

  static importJSON(serializedNode: SerializedMarkNode): MarkNode {
    return new MarkNode(serializedNode.ids, undefined, serializedNode.bgColor);
  }

  exportJSON(): SerializedMarkNode {
    return {
      ...super.exportJSON(),
      ids: Array.from(this.__ids),
      type: this.__type,
      bgColor: this.__bgColor ?? 'yellow'
    };
  }

  constructor(ids: string[] = NO_IDS, key?: NodeKey, bgColor?: string) {
    super(key);
    this.__ids = ids;
    this.__bgColor = bgColor
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = document.createElement('mark');

    // Add IDs as data attributes
    if (this.__ids.length > 0) {
      element.setAttribute('data-ids', this.__ids.join(','));
      addClassNamesToElement(element, config.theme.mark);
    } else {
      element.classList.add('PlaygroundEditorTheme__HighLightText')
    }

    if (this.__bgColor) {
      element.setAttribute('data-highlight', this.__bgColor);
    }

    return element;
  }

  updateDOM(prevNode: this, element: HTMLElement, config: EditorConfig): boolean {
    const prevIDs = prevNode.__ids;
    const nextIDs = this.__ids;

    if (prevIDs.join(',') !== nextIDs.join(',')) {
      element.setAttribute('data-ids', nextIDs.join(','));
    }

    if (prevNode.__bgColor !== this.__bgColor) {
      if (this.__bgColor) {
        element.setAttribute('data-highlight', this.__bgColor);
      }
    }

    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('mark');
    // if(element.textContent === ''){
    //   element.textContent = this.getTextContent();

    // }

    // Add IDs as data attributes
    if (this.__ids.length > 0) {
      element.setAttribute('data-ids', this.__ids.join(','));
    }
    if (this.__bgColor) {
      element.setAttribute('data-highlight', this.__bgColor);
    }
    return { element };
  }

  hasID(id: string): boolean {
    return this.__ids.includes(id);
  }

  getIDs(): Array<string> {
    return Array.from(this.__ids);
  }

  setIDs(ids: string[]): this {
    this.__ids = ids;
    return this;
  }

  addID(id: string): this {
    if (!this.__ids.includes(id)) {
      this.__ids = [...this.__ids, id];
    }
    return this;
  }

  deleteID(id: string): void {
    // Instead of re-assigning the array, just remove the entry in place:
    const idx = this.__ids.indexOf(id);
    if (idx > -1) {
      this.__ids.splice(idx, 1);
    }
  }

  setBgColor(value: string): this {
    this.__bgColor = value;
    return this;
  }

  getBgColor(): string | undefined {
    return this.__bgColor;
  }

  insertNewAfter(selection: RangeSelection, restoreSelection = true): null | ElementNode {
    const markNode = $createMarkNode(this.__ids, this.__bgColor);
    this.insertAfter(markNode, restoreSelection);
    return markNode;
  }

  canInsertTextBefore(): false {
    return false;
  }

  canInsertTextAfter(): false {
    return false;
  }

  canBeEmpty(): false {
    return false;
  }

  isInline(): true {
    return true;
  }
}

export function $createMarkNode(ids: string[] = NO_IDS, bgColor?: string): MarkNode {
  return $applyNodeReplacement(new MarkNode(ids, undefined, bgColor));
}

export function $isMarkNode(node: LexicalNode | null): node is MarkNode {
  return node instanceof MarkNode;
}
